import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

/**
 * WYSZUKIWANIA BEZ PODPOWIEDZI (Maciek 19.10: „super valuable — log it in a table so we can
 * periodically review this list, with a counter”). Gdy ktoś wpisze w pole obszaru frazę, dla
 * której nie ma ani jednej podpowiedzi, i przestanie pisać, fraza trafia do
 * `flat.area_search_misses` (migracja 20261019214414). Licznik na frazę liczy funkcja admina.
 *
 * Prywatność: tylko sama fraza, małymi literami — bez użytkownika, sesji i IP. Fraza z cyfrą
 * albo „@” nie jest wysyłana wcale (numer telefonu, numer domu, e-mail); baza odrzuca je też sama
 * (CHECK), to tutaj jest tylko po to, żeby nie wysyłać zapytania, które i tak padnie.
 */

/** Ile ms ciszy po ostatnim znaku, zanim fraza się liczy — pół-wpisane „Mokot” nie jest brakiem. */
export const CISZA_MS = 1500;

/** Postać zapisywana w bazie albo null, gdy fraza się nie nadaje (za krótka, za długa, cyfra, @). */
export function frazaDoZapisu(wpisane: string): string | null {
  const f = wpisane.trim().replace(/\s+/g, ' ').toLowerCase();
  if (f.length < 2 || f.length > 60) return null;
  if (/[0-9@]/.test(f)) return null;
  return f;
}

/** Raz na frazę na wizytę: ta sama fraza wpisana pięć razy to jeden brak, nie pięć. */
const zapisaneWTejWizycie = new Set<string>();

/** Tylko dla testów. */
export function wyczyscPamiecBrakow() {
  zapisaneWTejWizycie.clear();
}

export async function zapiszBrak(wpisane: string): Promise<boolean> {
  const fraza = frazaDoZapisu(wpisane);
  if (!fraza || zapisaneWTejWizycie.has(fraza)) return false;
  zapisaneWTejWizycie.add(fraza);
  // Bez `.select()`: tabela nie ma odczytu przez API (tylko INSERT kolumny `query`), więc
  // zapytanie musi zwrócić `return=minimal`. Błąd jest cichy — log nie może popsuć wyszukiwania.
  const { error } = await supabase
    .schema('flat' as never)
    .from('area_search_misses' as never)
    .insert({ query: fraza } as never);
  return !error;
}

/**
 * Zapisuje frazę, gdy `brak` (zero podpowiedzi) trwa CISZA_MS bez zmiany zapytania. Każdy
 * kolejny znak kasuje odliczanie, więc liczy się tylko to, na czym człowiek się zatrzymał.
 */
export function useZapisBrakuPodpowiedzi(zapytanie: string, brak: boolean) {
  useEffect(() => {
    if (!brak || !frazaDoZapisu(zapytanie)) return;
    const zegar = setTimeout(() => { void zapiszBrak(zapytanie); }, CISZA_MS);
    return () => clearTimeout(zegar);
  }, [zapytanie, brak]);
}
